/**
 * Event reports on the device (ADR-0029). A report is written once and queued
 * as a single idempotent `event.report` op: there is no server-side draft.
 * Its photos are queued first under the same entity (the report id), so the
 * outbox's per-entity order uploads them before the report that names them.
 * Until it is sent a report lives only in the encrypted outbox, which is also
 * where the Events screen reads unsent and refused reports from.
 */
import type { EventReportInput } from '@sss/events';
import type { OutboxItem } from '@sss/sync';
import type { OfflineStorage } from '../../lib/offline/types';
import type { SyncService } from '../../lib/sync/sync-service';
import { blobToArrayBuffer } from '../capture/draft-service';

export interface PendingReport {
  id: string;
  report: EventReportInput;
  state: 'PENDING' | 'REJECTED';
  error?: string;
  createdAt: number;
}

export interface ReportPhoto {
  id: string;
  blob: Blob;
  takenAt: string;
  lat?: number | null;
  lng?: number | null;
}

export async function queueReport(sync: SyncService, storage: OfflineStorage, report: EventReportInput, photos: ReportPhoto[] = []): Promise<void> {
  for (const [i, p] of photos.entries()) {
    await storage.putAttachment({
      id: p.id,
      surveyId: report.id,
      eventId: report.id,
      kind: 'PHOTO',
      ref: 'event',
      data: await blobToArrayBuffer(p.blob),
      contentType: p.blob.type || 'image/jpeg',
      fileName: `photo-${i + 1}.jpg`,
      size: p.blob.size,
      lat: p.lat ?? null,
      lng: p.lng ?? null,
      takenAt: p.takenAt,
      uploaded: false,
      createdAt: Date.now(),
    });
    await sync.enqueue('attachment.upload', report.id, { attachmentId: p.id }, { flush: false });
  }
  await sync.enqueue('event.report', report.id, photos.length ? { ...report, photos: photos.map((p) => p.id) } : report);
}

export async function pendingReports(storage: OfflineStorage): Promise<PendingReport[]> {
  return (await storage.all())
    .filter((i) => i.op === 'event.report')
    .sort((a, b) => b.createdAt - a.createdAt)
    .map((i) => ({ id: i.entityId, report: i.payload as EventReportInput, state: i.state, error: i.lastError, createdAt: i.createdAt }));
}

/** The report reached the server: its photos are there too, so free the space on the device. */
export async function onReportSent(storage: OfflineStorage, item: OutboxItem): Promise<void> {
  if (item.op !== 'event.report') return;
  for (const id of (item.payload as EventReportInput).photos ?? []) await storage.deleteAttachment(id);
}

/** Delete an unsent or refused report, with its photos. */
export async function discardReport(sync: SyncService, storage: OfflineStorage, p: PendingReport): Promise<void> {
  await sync.discard(p.id);
  for (const id of p.report.photos ?? []) await storage.deleteAttachment(id);
}
